import { useEffect, useState } from "react";
import { toast } from "sonner";
import client from "@/lib/api";
import { useProject } from "@/components/AppShell";
import { DownloadMdButton } from "@/components/DownloadMdButton";
import { buildDashboardMd } from "@/lib/mdBuilders";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, AreaChart, Area, CartesianGrid } from "recharts";
import { TrendingUp, TrendingDown, Zap, Search, ShieldAlert, Sparkles, Wand2, Loader2, RefreshCw, Rocket, Quote } from "lucide-react";
import { DataSourceBadge } from "@/components/DataSourceBadge";
import { RankDropExplainer } from "@/components/RankDropExplainer";
import { BulkDiagnose } from "@/components/BulkDiagnose";
import { CreditCost } from "@/components/CreditCost";
import { CreditActionButton } from "@/components/CreditActionButton";
import { useCosts } from "@/context/CostsContext";
import { DFSCostWidget } from "@/components/DFSCostWidget";
import { MetricInfo } from "@/components/MetricInfo";

const KPI = ({ label, value, delta, unit = "", testid, icon: Icon, metricKey }) => {
    const positive = delta > 0;
    return (
        <div data-testid={testid} className="border border-border p-6 bg-card/40 hover:border-primary/40 transition-colors">
            <div className="flex items-center justify-between">
                <div className="overline flex items-center gap-1.5">{label} {metricKey && <MetricInfo metricKey={metricKey} />}</div>
                {Icon && <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />}
            </div>
            <div className="mt-4 flex items-baseline gap-2">
                <span className="font-mono text-3xl">{value}</span>
                {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
            </div>
            {delta !== undefined && (
                <div className={`mt-2 text-xs font-mono flex items-center gap-1 ${positive ? "text-success" : "text-destructive"}`}>
                    {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {positive ? "+" : ""}{delta}%
                </div>
            )}
        </div>
    );
};

const severityColor = (s) => s === "error" ? "border-destructive/60 text-destructive" : s === "warning" ? "border-warning/50 text-warning" : "border-secondary/60 text-secondary";

export default function Dashboard() {
    const project = useProject();
    const { costs } = useCosts();
    const [data, setData] = useState(null);
    const [digest, setDigest] = useState(null);
    const [digestLoading, setDigestLoading] = useState(false);
    const [digestError, setDigestError] = useState(null);
    const [explainer, setExplainer] = useState({ open: false, id: null, label: "" });
    const [bulkOpen, setBulkOpen] = useState(false);

    const loadDashboard = () => {
        if (!project) return;
        client.get(`/projects/${project.id}/dashboard`).then((r) => setData(r.data));
    };

    useEffect(() => {
        loadDashboard();
        setDigest(null);
        setDigestError(null);
    }, [project]);

    const runDigest = async () => {
        setDigestLoading(true);
        setDigestError(null);
        try {
            const r = await client.get(`/projects/${project.id}/ai-digest`);
            setDigest(r.data);
        } catch (e) {
            setDigestError(e?.response?.data?.detail || "Could not build the digest");
        } finally {
            setDigestLoading(false);
        }
    };

    const pullAllLiveData = async () => {
        const jobs = [];
        if (data?.kpis?.total_keywords > 0) {
            jobs.push(
                client.post(`/projects/${project.id}/keywords/refresh-serp`)
                    .then((r) => `${r.data.refreshed} keywords`)
                    .catch(() => null)
            );
        }
        jobs.push(
            client.post(`/projects/${project.id}/backlinks/refresh`)
                .then((r) => (r.data.available ? "backlinks" : null))
                .catch(() => null)
        );
        jobs.push(
            client.post(`/projects/${project.id}/competitors/refresh`)
                .then((r) => (r.data.available ? "competitors" : null))
                .catch(() => null)
        );
        const results = await Promise.allSettled(jobs);
        const pulled = results.filter((r) => r.status === "fulfilled" && r.value).map((r) => r.value);
        loadDashboard();
        toast.success(pulled.length ? `Pulled live data: ${pulled.join(", ")}` : "Nothing to pull yet — add keywords or competitors first");
    };

    if (!project || !data) return <div className="text-muted-foreground font-mono text-sm">loading dashboard…</div>;
    const k = data.kpis;
    const pullAllCost = Math.round(
        (costs?.keyword_add || 1) * (k.total_keywords || 0) + (costs?.backlinks_refresh || 0) + (costs?.competitors_refresh || 0)
    );

    return (
        <div className="space-y-8 animate-fade-in" data-testid="dashboard">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="overline">Overview · {project.domain}</div>
                    <h1 className="font-display text-3xl md:text-4xl tracking-tight mt-2">Rank velocity, at a glance.</h1>
                </div>
                <div className="flex items-center gap-2">
                    <CreditActionButton
                        cost={pullAllCost}
                        testid="pull-all-live-data-btn"
                        variant="outline"
                        onConfirm={pullAllLiveData}
                        description="Refreshes live SERP data for every tracked keyword, plus backlinks and competitors where those features are unlocked. Actions already cached within the last 24h are free."
                    >
                        <span className="inline-flex items-center gap-1">
                            <RefreshCw className="w-3.5 h-3.5" /> Pull all live data
                        </span>
                    </CreditActionButton>
                    <DownloadMdButton
                        filenamePrefix={`inphase-dashboard-${project?.domain || "project"}`}
                        buildMarkdown={() => buildDashboardMd({ project, data, digest })}
                        testId="download-dashboard-md"
                    />
                </div>
            </div>

            {!data.has_data && (
                <div className="border border-primary/60 bg-primary/5 p-6 flex items-center justify-between gap-4 flex-wrap" data-testid="no-data-banner">
                    <div className="flex items-center gap-3">
                        <Rocket className="w-6 h-6 text-primary shrink-0" strokeWidth={1.5} />
                        <div>
                            <div className="font-display text-lg">No live data yet for this project.</div>
                            <div className="text-sm text-muted-foreground mt-0.5">Pull real rankings, backlinks, and competitor data from DataForSEO in one click.</div>
                        </div>
                    </div>
                    <CreditActionButton
                        cost={pullAllCost}
                        testid="pull-all-live-data-btn-banner"
                        onConfirm={pullAllLiveData}
                        description="Refreshes live SERP data for every tracked keyword, plus backlinks and competitors where those features are unlocked. Actions already cached within the last 24h are free."
                    >
                        <span className="inline-flex items-center gap-1">
                            <RefreshCw className="w-4 h-4" /> Pull all live data
                        </span>
                    </CreditActionButton>
                </div>
            )}

            {/* AI Weekly Digest */}
            <div className="border border-primary/40 bg-primary/5 p-6" data-testid="ai-digest-section">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <div className="flex items-center gap-3">
                            <div className="overline text-primary">AI Weekly Digest</div>
                            {digest && <DataSourceBadge source={digest.data_source === "live" ? "dataforseo" : "mock"} />}
                        </div>
                        <div className="font-display text-xl mt-1">What matters this week, in plain English.</div>
                        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                            Claude reads your live rankings, backlinks, and traffic and writes a terse action list. Emailed every Monday.
                        </p>
                    </div>
                    <button
                        data-testid="run-ai-digest-btn"
                        onClick={runDigest}
                        disabled={digestLoading}
                        className="bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60"
                    >
                        {digestLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                        {digest ? "Regenerate" : "Generate now"}
                        <span className="opacity-70 text-[10px] font-mono">· <CreditCost action="digest" size="xs" className="!text-primary-foreground/70" /></span>
                    </button>
                </div>
                {digestError && (
                    <div className="mt-4 text-sm text-destructive border border-destructive/40 bg-destructive/5 p-3" data-testid="ai-digest-error">
                        {digestError}
                    </div>
                )}
                {digest && digest.digest_text && (
                    <pre data-testid="ai-digest-output"
                         className="mt-5 whitespace-pre-wrap font-sans text-sm text-foreground bg-background/60 border border-border p-5 leading-relaxed">
{digest.digest_text}
                    </pre>
                )}
                {digest && !digest.digest_text && (
                    <div className="mt-5 text-sm text-muted-foreground border border-border p-4">
                        AI text generation temporarily unavailable. Your live data is still shown across the app.
                    </div>
                )}
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <KPI testid="kpi-visibility" label="Visibility Index" value={k.visibility} unit="%" delta={k.visibility_delta} icon={Zap} metricKey="visibility_index" />
                <KPI testid="kpi-traffic" label="Est. Organic Traffic" value={k.estimated_traffic.toLocaleString()} delta={k.traffic_delta} icon={TrendingUp} metricKey="estimated_traffic" />
                <KPI testid="kpi-keywords" label="Tracked Keywords" value={k.total_keywords} icon={Search} metricKey="tracked_keywords" />
                <KPI testid="kpi-ai" label="AI Overview Coverage" value={k.ai_overviews} icon={Sparkles} metricKey="ai_overview_coverage" />
                <KPI testid="kpi-ai-citations" label="AI Citations" value={k.ai_citations ?? 0} icon={Quote} metricKey="ai_overview_citation" />
            </div>

            {/* DFS Spend widget — self-hides for non-admin/agency users.
                Two side-by-side widgets: this project + account-wide. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <DFSCostWidget projectId={project.id} />
                <DFSCostWidget />
            </div>

            {/* Trend + Alerts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 border border-border p-6 bg-card/40">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <div className="overline flex items-center gap-1.5">Visibility · 30d <MetricInfo metricKey="visibility_index" /></div>
                            <div className="font-mono text-2xl mt-1">{k.visibility}%</div>
                        </div>
                        <div className="text-xs text-muted-foreground">Traffic estimate overlay</div>
                    </div>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={data.trend_30d}>
                                <defs>
                                    <linearGradient id="vis" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="hsl(196 100% 61%)" stopOpacity={0.6} />
                                        <stop offset="100%" stopColor="hsl(196 100% 61%)" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="#1a1a1a" vertical={false} />
                                <XAxis dataKey="day" stroke="#555" fontSize={10} />
                                <YAxis stroke="#555" fontSize={10} />
                                <Tooltip
                                    contentStyle={{ background: "#0a0a0a", border: "1px solid #2a2a2a", borderRadius: 0, fontSize: 12 }}
                                    itemStyle={{ color: "#38cfff" }}
                                />
                                <Area type="monotone" dataKey="visibility" stroke="hsl(196 100% 61%)" strokeWidth={1.5} fill="url(#vis)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="border border-border p-6 bg-card/40" data-testid="alerts-panel">
                    <div className="overline mb-4">Live Alerts</div>
                    <div className="space-y-3">
                        {data.alerts.length === 0 ? (
                            <div className="text-sm text-muted-foreground">All clear.</div>
                        ) : data.alerts.map((a) => (
                            <div key={a.id} className={`border-l-2 pl-3 py-1 ${severityColor(a.severity)}`}>
                                <div className="text-sm font-medium">{a.title}</div>
                                <div className="text-xs text-muted-foreground mt-0.5">{a.body}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Toxic + Top movers */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="border border-border p-6 bg-card/40">
                    <div className="flex items-center gap-2 mb-3">
                        <ShieldAlert className="w-4 h-4 text-destructive" />
                        <div className="overline flex items-center gap-1.5">Toxic Backlinks <MetricInfo metricKey="toxic_backlinks" /></div>
                    </div>
                    <div className="font-mono text-3xl text-destructive">{k.toxic_links}</div>
                    <div className="text-xs text-muted-foreground mt-2">Ready for disavow export</div>
                </div>

                <div className="border border-border p-6 bg-card/40">
                    <div className="overline mb-3 flex items-center gap-1.5">Top-3 vs Top-10 <MetricInfo metricKey="top3_top10" /></div>
                    <div className="flex items-baseline gap-3">
                        <div>
                            <div className="text-xs text-muted-foreground">Top 3</div>
                            <div className="font-mono text-2xl text-primary">{k.top3}</div>
                        </div>
                        <div className="text-muted-foreground">/</div>
                        <div>
                            <div className="text-xs text-muted-foreground">Top 10</div>
                            <div className="font-mono text-2xl">{k.top10}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Top movers */}
            <div className="border border-border bg-card/40" data-testid="top-movers-table">
                <div className="p-6 border-b border-border flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="overline">Rank changes · at a glance</div>
                        <div className="text-sm text-muted-foreground mt-1">Biggest ranking shifts across your keyword portfolio (last 7 days)</div>
                    </div>
                    <button
                        data-testid="dashboard-bulk-diagnose-btn"
                        onClick={() => setBulkOpen(true)}
                        className="border border-primary/60 text-primary hover:bg-primary/10 text-xs font-medium px-3 py-2 flex items-center gap-2"
                        title="Bulk diagnose costs 3 credits + 1 per keyword analyzed"
                    >
                        <Wand2 className="w-3.5 h-3.5" /> Diagnose all losers
                        <span className="opacity-70">·</span> <CreditCost action="bulk_diagnose_base" size="xs" className="!text-primary/70" />
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-xs text-muted-foreground">
                            <tr className="border-b border-border">
                                <th className="text-left p-3">Keyword</th>
                                <th className="text-right p-3">Position</th>
                                <th className="text-right p-3">Change</th>
                                <th className="text-right p-3">Volume</th>
                                <th className="text-right p-3">Intent</th>
                                <th className="p-3 w-8"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.top_movers.map((m, i) => (
                                <tr key={i} className="border-b border-border/50 hover:bg-accent/20">
                                    <td className="p-3 font-mono">{m.keyword || "—"}</td>
                                    <td className="p-3 text-right font-mono">{m.position}</td>
                                    <td className={`p-3 text-right font-mono ${m.change > 0 ? "text-success" : m.change < 0 ? "text-destructive" : "text-muted-foreground"}`}>
                                        {m.change > 0 ? "+" : ""}{m.change}
                                    </td>
                                    <td className="p-3 text-right font-mono text-muted-foreground">{m.volume.toLocaleString()}</td>
                                    <td className="p-3 text-right text-xs text-muted-foreground">{m.intent}</td>
                                    <td className="p-3 text-right">
                                        {m.id && (
                                            <button
                                                data-testid={`dashboard-explain-${m.id}`}
                                                onClick={() => setExplainer({ open: true, id: m.id, label: m.keyword })}
                                                title="Ask Claude to diagnose this move"
                                                className="text-muted-foreground hover:text-primary"
                                            >
                                                <Wand2 className="w-4 h-4" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <RankDropExplainer
                open={explainer.open}
                onOpenChange={(v) => setExplainer((s) => ({ ...s, open: v }))}
                projectId={project.id}
                keywordId={explainer.id}
                keywordLabel={explainer.label}
            />
            <BulkDiagnose
                open={bulkOpen}
                onOpenChange={setBulkOpen}
                projectId={project.id}
            />
        </div>
    );
}
